import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check, X, ChevronDown, BarChart3, Users, Lightbulb, MessageCircle, Eye, GitBranch, PenLine, ListChecks } from "lucide-react";
import { useState } from "react";

const CAL_LINK = "https://cal.com/kalza-studio-hdho6x";

const Highlight = ({ children }) => (
  <span className="relative inline-block font-accent">
    <span className="absolute left-0 right-0 bottom-[0.08em] h-[0.8em] bg-[#BE123C] -z-0" />
    <span className="relative z-10">{children}</span>
  </span>
);

const OrganicImage = ({ src, alt, className = "" }) => (
  <div className={`aspect-[4/5] overflow-hidden ${className}`} style={{ WebkitMaskImage: 'url(/images/mood/shape-mask.png)', maskImage: 'url(/images/mood/shape-mask.png)', WebkitMaskSize: '100% 100%', maskSize: '100% 100%', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat' }}>
    <img src={src} alt={alt || ""} className="w-full h-full object-cover" />
  </div>
);

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#1C1210]/10 last:border-0">
      <button onClick={() => setOpen(!open)} className="w-full flex items-start justify-between gap-4 py-5 text-left">
        <span className="font-serif text-lg text-[#1C1210]">{q}</span>
        <ChevronDown size={18} className={`text-[#BE123C] flex-shrink-0 mt-1 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="font-body text-sm text-[#6B6560] leading-relaxed pb-5">{a}</p>}
    </div>
  );
};

const LOGOS = [
  { src: "/images/brand-articulations.png", alt: "Brand Articulations" },
  { src: "/images/uefa.png", alt: "UEFA Champions League" },
  { src: "/images/pyszne.png", alt: "Pyszne.pl" },
  { src: "/images/coca-cola.png", alt: "Coca-Cola" },
  { src: "/images/lukkien.png", alt: "Lukkien" },
  { src: "/images/just-eat.png", alt: "Just Eat Takeaway" },
  { src: "/images/house-of-retouching.png", alt: "House of Retouching" },
  { src: "/images/samsung.png", alt: "Samsung" },
  { src: "/images/ben-jerrys.png", alt: "Ben & Jerry\'s" },
  { src: "/images/heineken.png", alt: "Heineken" },
];

const testimonials = [
  {
    id: 1,
    quote: "Mieliśmy wydać 8 000 euro na nową stronę, kiedy ktoś zasugerował, żebyśmy najpierw zrobili ten sprint. Okazało się, że problem w ogóle nie leżał w stronie. Ten jeden insight uratował nas przed bardzo kosztownym błędem.",
    author: "Julia Werner",
    role: "Współzałożycielka marki wellness",
    initials: "JW",
  },
  {
    id: 2,
    quote: "Ciągle sobie powtarzałem, że zajmę się marką później, po następnym launchu, po następnym kliencie. Minęło sześć miesięcy i nic się nie zmieniło. Dwie godziny z Olą i tydzień później miałem wszystko zmapowane. Powinienem był to zrobić rok temu.",
    author: "Tomek Nowicki",
    role: "Trener fitness i twórca kursów online",
    initials: "TN",
  },
  {
    id: 3,
    quote: "Byłam tak zmęczona poprawianiem marki co kilka tygodni i ciągłym uczuciem, że coś jest nie tak. Ten sprint dał mi jasność, której potrzebowałam tuż przed dużą inwestycją w biznes. To była najlepsza decyzja, jaką mogłam wtedy podjąć.",
    author: "Marta Kowalska",
    role: "Właścicielka studia projektowania wnętrz",
    initials: "MK",
  },
];

export default function SprintPagePL() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isTestimonialAnimating, setIsTestimonialAnimating] = useState(false);

  const handleTestimonialSelect = (index) => {
    if (index === activeTestimonial || isTestimonialAnimating) return;
    setIsTestimonialAnimating(true);
    setTimeout(() => {
      setActiveTestimonial(index);
      setTimeout(() => setIsTestimonialAnimating(false), 400);
    }, 200);
  };

  const currentTestimonial = testimonials[activeTestimonial];

  return (
    <main data-testid="sprint-page-pl">
      {/* ═══ 1. HERO ═══ */}
      <section className="pt-32 pb-20 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-8 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-3">
              <span className="font-eyebrow text-white mb-6 block">DIAGNOSTYKA TWOJEJ MARKI</span>
              <h1 className="mb-6">
                <span className="font-display text-4xl md:text-5xl lg:text-6xl text-white block leading-[0.95]">BRAND REALITY CHECK</span>
                <span className="font-accent text-2xl md:text-3xl lg:text-4xl text-[#BE123C] block leading-[1.1] mt-2">uczciwe spojrzenie, którego unikasz</span>
              </h1>
              <p className="font-body text-base md:text-lg text-white/60 leading-relaxed max-w-xl mb-8">
                Strategiczna diagnostyka dla osób prowadzących własny biznes, które nigdy nie mają czasu spojrzeć na swoją markę z zewnątrz. Pokazuję Ci co naprawdę widzą obcy, jak wypadasz na tle konkurencji i co naprawić w pierwszej kolejności. Pełna jasność w 7 dni.
              </p>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="lg:hidden flex justify-center mb-10">
                <img src="/images/report-mockup.png" alt="Brand Reality Report" className="w-full max-w-xs" />
              </motion.div>
              <div className="flex flex-wrap gap-4 mb-10">
                {["Twój wkład: 2 godziny", "Pełny raport w 7 dni", "Analiza konkurencji w cenie", "30-dniowy plan działania"].map((label, i) => (
                  <div key={i} className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-full px-4 py-2">
                    <Check size={12} className="text-[#D4A0A0]" />
                    <span className="font-body text-xs text-white/60 tracking-wide">{label}</span>
                  </div>
                ))}
              </div>
              <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-bold text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#BE123C] transition-colors duration-300">
                ZAMÓW SPRINT <ArrowRight size={16} />
              </a>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="hidden lg:block lg:col-span-2">
              <img src="/images/report-mockup.png" alt="Brand Reality Report" className="w-full scale-125 origin-center" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 2. PROBLEM ═══ */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-6 block">PROBLEM</span>
              <h2 className="mb-8">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">COŚ PRZESTAŁO</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">do siebie pasować</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7] max-w-lg">
                <p>Twoi klienci zostają, polecają Cię, piszą podziękowania. No ale kiedy ostatnio otworzyłaś swoją stronę i pomyślałaś "tak, to jest dokładnie to"?</p>
                <p>Wiesz, że Twój biznes jest gotowy na więcej. Jakość jest. Wyniki są. No ale marka nie nadąża. Właściwi klienci są gdzieś tam, tylko Twoja marka do nich jeszcze nie dociera. Ten sprint pokazuje Ci dokładnie dlaczego i co z tym zrobić.</p>
                <p>Siedem dni, pełna jasność.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="lg:pt-16">
              <span className="font-eyebrow text-[#8B1A3A] mb-8 block">ROZPOZNAJESZ TO?</span>
              <div className="space-y-5">
                {[
                  "W zeszłym tygodniu otworzyłaś swoją stronę i od razu przyszło Ci do głowy pięć rzeczy do zmiany",
                  "Twój biznes urósł, no ale marka wciąż wygląda jak wersja z pierwszych sześciu miesięcy",
                  "Chcesz podnieść ceny, no ale kiedy wyobrażasz sobie nową kwotę na stronie, coś w żołądku się ściska",
                  "W zeszłym miesiącu wrzuciłaś rolkę, która była bezpieczna i generyczna, i wiedziałaś o tym już w trakcie montażu",
                  "Twoja strona i Instagram wyglądają jakby należały do dwóch różnych biznesów",
                  "Właściwi klienci są gdzieś tam, tylko Twoja marka do nich nie dociera",
                  "Na ostatnim spotkaniu networkingowym ktoś zapytał co robisz i odpowiedziałaś inaczej niż to co jest na Twojej stronie",
                ].map((sign, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={16} className="text-[#BE123C] flex-shrink-0 mt-1" />
                    <span className="font-body text-base text-[#4A4540]">{sign}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ DIVIDER 1 ═══ */}
      <section className="relative py-14 md:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/40" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-snug">
              Twoja nowa <Highlight>niesprawiedliwa przewaga</Highlight>.
            </p>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-snug mt-1">
              Lata eksperymentowania skompresowane w jeden strategiczny skrót.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ 3. JAK TO DZIAŁA ═══ */}
      <section className="py-24 md:py-32 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-6 block">STRATEGICZNY PRZYSTANEK</span>
            <h2 className="mb-4">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">JAK TO DZIAŁA</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">w czterech etapach</span>
            </h2>
            <p className="font-body text-base text-[#6B6560] leading-[1.7] max-w-2xl mb-6">
              Otwieram Twoją stronę tak, jak zrobiłby to obcy człowiek. Bez kontekstu, bez historii, tylko pierwsze wrażenie. Potem porównuję to, co mówią o Tobie Twoi klienci, z tym co mówi Twoja strona główna. Luka między tymi dwoma to miejsce, w którym żyje problem.
            </p>
            <p className="font-body text-base text-[#4A4540] leading-[1.7] max-w-2xl mb-16">
              Każde odkrycie jest powiązane z konsekwencją biznesową. Jeśli Twoja strona cenowa wysyła mieszane sygnały, mówię Ci ile Cię to kosztuje. To jest diagnostyka strategiczna, nie przegląd designu.
            </p>
          </motion.div>

          <div className="relative">
            <div className="hidden lg:block absolute left-8 top-0 bottom-24 w-px bg-[#BE123C]/20" />
            <div className="space-y-12 lg:space-y-16">
              {[
                { num: "01", title: "Formularz wstępny", subtitle: "~30 min", body: "Wypełniasz kwestionariusz o swoim biznesie: co sprzedajesz, kim są Twoi klienci i gdzie czujesz, że coś się zacinaja. To daje mi kontekst, zanim spojrzę na Twoją markę z zewnątrz." },
                { num: "02", title: "Rozmowa strategiczna 1:1", subtitle: "45 min · online", body: "Siadamy i słucham. Kiedy mówisz o swoich klientach, Twoje oczy się zapalają. Kiedy pytam o cennik, wahasz się. Ten rozjazd między tym co mówisz a tym jak to mówisz to miejsce, w którym żyje prawdziwy problem." },
                { num: "03", title: "Pogłębiona analiza marki", subtitle: "tu dzieje się sprint", body: "Twoja strona staje obok trzech do pięciu konkurentów. Czytam opinie Twoich klientów i wyciągam słowa, których używają żeby Cię opisać. Twoja strona główna przechodzi test 5-sekundowej uwagi, żeby zobaczyć co obcy zauważa najpierw. Sprawdzam czy Twoje bio na Instagramie zgadza się z Twoją stroną ofertową. To wszystko według metodologii budowanej przez 15 lat na markach od startupów po globalne korporacje." },
                { num: "04", title: "Rozmowa podsumowująca 1:1", subtitle: "45 min · online", body: "Dostajesz pełny Brand Reality Report. Przechodzimy przez każde odkrycie na ekranie, żebyś widziała co ja zobaczyłam, dlaczego to ma znaczenie i od czego zacząć. Większość osób tego samego wieczoru otwiera laptopa i zaczyna wprowadzać zmiany." },
              ].map((step, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }} className="relative lg:pl-24">
                  <div className="hidden lg:flex absolute left-5 top-1 w-7 h-7 rounded-full bg-white border-2 border-[#BE123C]/30 items-center justify-center">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#BE123C]" />
                  </div>
                  <div className="flex items-baseline gap-4 mb-3">
                    <span className="font-accent text-3xl text-[#BE123C]/25">{step.num}</span>
                    <div>
                      <h3 className="font-serif text-xl text-[#1C1210]">{step.title}</h3>
                      {step.subtitle && <span className="font-body text-xs text-[#6B6560]">{step.subtitle}</span>}
                    </div>
                  </div>
                  <p className="font-body text-sm text-[#6B6560] leading-relaxed max-w-xl lg:ml-12">{step.body}</p>
                </motion.div>
              ))}
            </div>
            <p className="font-eyebrow text-[#8B1A3A] text-center mt-16">REALIZACJA W 7 DNI ROBOCZYCH</p>
          </div>
        </div>
      </section>

      {/* ═══ JAK MYŚLĘ ═══ */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/kalza-bg.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-6 block">JAK MYŚLĘ</span>
              <h3 className="mb-10 max-w-lg">
                <span className="font-display text-2xl md:text-3xl lg:text-4xl text-[#1C1210] leading-[0.95]">WIĘKSZOŚĆ WŁAŚCICIELI BIZNESU MYŚLI, ŻE ICH PROBLEM TO INSTAGRAM ALBO LOGO. </span>
                <span className="font-accent text-2xl md:text-3xl lg:text-4xl text-[#BE123C] leading-[1.1]">Nie jest.</span>
              </h3>
              <div className="space-y-5 font-body text-base text-[#6B6560] leading-[1.7]">
                <p>Kiedy diagnozuję Twoją markę, analizuję cztery źródła prawdy:</p>
                <div className="space-y-3 pl-1">
                  {[
                    "Co widzi obcy przy pierwszym kontakcie z Twoim biznesem",
                    "Co Ty jako właścicielka wierzysz, że sprzedajesz",
                    "Co Twoi płacący klienci naprawdę o Tobie mówią",
                    "Co Twoja konkurencja sygnalizuje na tym samym rynku",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="font-accent text-lg text-[#BE123C]/40 mt-0.5 flex-shrink-0">{i + 1}.</span>
                      <span className="font-body text-base text-[#4A4540]">{item}</span>
                    </div>
                  ))}
                </div>
                <p>Pomyśl o tym jak o czterech kawałkach puzzli, które powinny tworzyć jeden jasny obraz. Kiedy kawałki pasują, ludzie po prostu to łapią. Kiedy nie pasują, potencjalni klienci czują, że coś jest nie tak i wychodzą. Moim zadaniem jest pokazać Ci co nie gra.</p>
                <p>To jak łączę te kropki opiera się na autorskiej metodologii zbudowanej na archetypach jungowskich, ekonomii behawioralnej, psychologii narracji, semiotyce, Human Design i Wealth Dynamics oraz ogólnej pasji do ludzkiego umysłu. Każde podejście daje mi inny kąt patrzenia na to, dlaczego Twoja marka wywołuje u ludzi takie a nie inne uczucie.</p>
                <p>Kiedy przeczytasz raport, mgła się rozwiewa. Zyskujesz świadomość tego co naprawdę dzieje się w Twoim biznesie. Czujesz nową energię, bo wiesz dokładnie co zrobić dalej.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="hidden lg:block lg:pt-16">
              <img src="/images/mood/ola-process.jpg" alt="Aleksandra pracująca nad strategią marki" className="w-full shadow-lg" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 4. CO DOSTAJESZ ═══ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-6 block">RAPORT ZBUDOWANY DO DZIAŁANIA</span>
            <h2 className="mb-8">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">CO DOSTAJESZ</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">Twój Brand Reality Check Report</span>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start mb-10">
              <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="order-2 lg:order-1 lg:pt-2">
                <div className="font-body text-base text-[#6B6560] leading-[1.7]">
                  <p className="mb-4">Twój Brand Reality Check Report jest zbudowany wokół trzech prostych pytań:</p>
                  <div className="space-y-3 pl-1 mb-4">
                    <div className="flex items-start gap-3">
                      <span className="font-accent text-lg text-[#BE123C]/40 mt-0.5 flex-shrink-0">1.</span>
                      <span className="text-[#4A4540]">Gdzie jesteś teraz? Jak Twoja marka jest postrzegana przez ludzi, którzy nigdy o Tobie nie słyszeli.</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-accent text-lg text-[#BE123C]/40 mt-0.5 flex-shrink-0">2.</span>
                      <span className="text-[#4A4540]">Gdzie powinnaś być? Jak Twoja marka powinna wyglądać na podstawie tego, co odkryłam o Twoim biznesie, klientach i rynku.</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-accent text-lg text-[#BE123C]/40 mt-0.5 flex-shrink-0">3.</span>
                      <span className="text-[#4A4540]">Jaki jest plan? Konkretne kroki, które możesz zacząć wdrażać dzisiaj, bez potrzeby zatrudniania grafika czy programisty na start.</span>
                    </div>
                  </div>
                  <p>Obejmuje dwie sesje 1:1 (45 min każda): rozmowę strategiczną przed analizą i rozmowę podsumowującą po. Oto co zawiera Twój raport:</p>
                </div>
              </motion.div>
              <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="order-1 lg:order-2 lg:-mt-48">
                <img src="/images/report-mockup.png" alt="Brand Reality Check Report" className="w-full" />
              </motion.div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: BarChart3, title: "Diagnostyka marki w 6 wymiarach", body: "Punktacja dla sześciu obszarów Twojej marki. Widzisz dokładny wynik dla każdego i wiesz, który naprawić w pierwszej kolejności" },
                { icon: Users, title: "Analiza konkurencji", body: "Twoja strona obok trzech do pięciu konkurentów. Widzisz dokładnie gdzie się wtapiasz i gdzie się wyróżniasz" },
                { icon: Lightbulb, title: "\"Co naprawdę sprzedajesz\"", body: "Luka między tym, co myślisz że sprzedajesz a tym, za co ludzie Ci płacą. Jedna z moich klientek odkryła, że jej klienci kupowali 'spokój ducha' podczas gdy jej strona sprzedawała 'usługi projektowania wnętrz'" },
                { icon: MessageCircle, title: "Analiza luk komunikacyjnych", body: "Konkretne zdania na Twojej stronie, w których potencjalny klient przestaje czytać i wychodzi. Zaznaczam każde i wyjaśniam co powinno tam być zamiast tego" },
                { icon: Eye, title: "Analiza uwagi wizualnej", body: "Gdzie ludzie naprawdę patrzą na Twojej stronie w pierwszych 5 sekundach. Technologia eye-trackingowa zweryfikowana przez MIT. Widzisz co jest zauważane, co ignorowane i co dokąd przenieść" },
                { icon: GitBranch, title: "Mapa napięć strategicznych", body: "Twoja strona O mnie mówi jedno, strona ofertowa drugie, a opinie trzecie. Ta mapa pokazuje to wszystko naraz, żebyś wiedziała co naprawić w pierwszej kolejności" },
                { icon: PenLine, title: "Szczery briefing od mnie", body: "Moja bezpośrednia ocena tego co widzę, co to oznacza dla Twojego przychodu i co zrobiłabym w tym tygodniu gdyby to był mój biznes" },
                { icon: ListChecks, title: "30-dniowy plan działania", body: "Pięć konkretnych kroków uszeregowanych według wpływu, które możesz wdrożyć w tym tygodniu bez zatrudniania grafika czy programisty" },
              ].map((item, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }} className="py-4">
                  <item.icon size={22} className="text-[#BE123C] mb-4" />
                  <h3 className="font-serif text-base text-[#1C1210] mb-2">{item.title}</h3>
                  <p className="font-body text-sm text-[#6B6560] leading-relaxed">{item.body}</p>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-12">
              <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-bold text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#BE123C] transition-colors duration-300">
                CHCĘ TO DLA MOJEJ MARKI <ArrowRight size={16} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ DIVIDER 2 ═══ */}
      <section className="relative py-14 md:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/40" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-snug">
              Większość właścicieli biznesu spędza miesiące zgadując, co jest nie tak z ich marką. Ten sprint daje Ci odpowiedź w <Highlight>7 dni</Highlight>
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ 5. CO SIĘ ZMIENIA ═══ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-6 block">JASNOŚĆ PRZYCHODZI</span>
            <h2 className="mb-6">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">CO SIĘ ZMIENIA</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">po sprincie</span>
            </h2>
            <p className="font-body text-base text-[#6B6560] leading-[1.7] max-w-2xl mb-12">
              Każdy kto przeszedł ten sprint mówi mi to samo: żałuje, że nie zrobił tego wcześniej.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#1C1210] mb-6 block">OD RAZU</span>
              <div className="space-y-5">
                {[
                  "Widzisz jasną punktację dla każdego obszaru swojej marki i wiesz, który wzmocnić w pierwszej kolejności",
                  "Znajdujesz trzy zdania na swojej stronie głównej, które sprawiały że ludzie wychodzili zanim się w ogóle odezwali",
                  "Słyszysz to, czego nikt w Twoim otoczeniu by Ci nie powiedział o tym jak Twoja marka jest odbierana",
                  "Wiesz co zrobić w poniedziałek rano bez pytania pięciu osób o zdanie",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={18} className="text-[#BE123C] flex-shrink-0 mt-1" />
                    <span className="font-body text-base text-[#4A4540] leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}>
              <span className="font-eyebrow text-[#1C1210] mb-6 block">W CIĄGU 30 DNI</span>
              <div className="space-y-5">
                {[
                  "Wiesz gdzie wyprzedzasz konkurencję i gdzie ona Cię bije",
                  "Przepisujesz swoją ofertę, bo odkrywasz że Twoi klienci cenią coś innego niż to co reklamujesz",
                  "Przenosisz swój najsilniejszy argument sprzedażowy na pierwsze miejsce, które ludzie widzą",
                  "Twoja strona i Instagram w końcu mówią to samo",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={18} className="text-[#BE123C] flex-shrink-0 mt-1" />
                    <span className="font-body text-base text-[#4A4540] leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ TESTIMONIALE ═══ */}
      <section className="py-16 md:py-24 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="flex flex-col items-center gap-10 py-8">
              <div className="relative px-8 max-w-3xl">
                <span className="absolute -left-2 -top-6 text-7xl font-serif text-[#2C2C2A]/10 select-none pointer-events-none">"</span>
                <p className={`text-lg md:text-2xl font-light text-[#2C2C2A] text-center leading-relaxed transition-all duration-500 ease-out ${isTestimonialAnimating ? 'opacity-0 blur-sm scale-[0.98]' : 'opacity-100 blur-0 scale-100'}`}>
                  {currentTestimonial.quote}
                </p>
                <span className="absolute -right-2 -bottom-8 text-7xl font-serif text-[#2C2C2A]/10 select-none pointer-events-none">"</span>
              </div>
              <div className="flex flex-col items-center gap-6 mt-2">
                <p className={`text-xs text-[#6B6560] tracking-[0.2em] uppercase transition-all duration-500 ease-out ${isTestimonialAnimating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>
                  {currentTestimonial.role}
                </p>
                <div className="flex items-center justify-center gap-2">
                  {testimonials.map((t, index) => {
                    const isActive = activeTestimonial === index;
                    return (
                      <button key={t.id} onClick={() => handleTestimonialSelect(index)} className={`relative flex items-center gap-0 rounded-full cursor-pointer transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${isActive ? 'bg-[#2C2C2A] shadow-lg pr-4 pl-2 py-2' : 'bg-transparent hover:bg-[#f5f5f7] p-0.5'}`}>
                        <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-medium transition-all duration-500 ${isActive ? 'bg-[#BE123C] text-white ring-2 ring-white/30' : 'bg-[#e5e2de] text-[#6B6560] hover:scale-105'}`}>{t.initials}</div>
                        <div className={`grid transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${isActive ? 'grid-cols-[1fr] opacity-100 ml-2' : 'grid-cols-[0fr] opacity-0 ml-0'}`}>
                          <div className="overflow-hidden"><span className={`text-sm font-medium whitespace-nowrap block transition-colors duration-300 ${isActive ? 'text-white' : 'text-[#2C2C2A]'}`}>{t.author}</span></div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ 6. KTO ZA TYM STOI ═══ */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/kalza-bg.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <OrganicImage src="/images/ola-about.jpg" alt="Aleksandra Kałuża" className="shadow-2xl" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">KTO ZA TYM STOI</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">ALEKSANDRA KAŁUŻA</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">KALZA Studio</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7]">
                <p>Studiowałam sztuki piękne w Irlandii i Polsce, zanim spędziłam lata wewnątrz międzynarodowych firm w Europie, prowadząc zespoły grafików i copywriterów i nadzorując wszystko, co docierało do klienta na wielu rynkach jednocześnie. Wiem co musi mówić billboard, zanim zostanie wywieszony. Moim zadaniem było zrozumieć co zarząd chce zakomunikować i dopilnować, żeby prawdziwy człowiek na ulicy to faktycznie poczuł.</p>
                <p>Z czasem dodałam do tego własną warstwę. Głęboką ciekawość psychologii, emocji i tego dlaczego ludzie ufają jednemu biznesowi bardziej niż drugiemu, kiedy oba dostarczają tę samą jakość. To połączenie doświadczenia strategicznego i rozumienia ludzkiego umysłu stało się procesem, którego używam dzisiaj.</p>
                <p>Kiedy patrzę na Twoją markę, korzystam z tego wszystkiego naraz. Wytrenowane oko, instynkt kreatywny i lata obserwowania co naprawdę sprawia, że ludzie się zatrzymują i zwracają uwagę.</p>
              </div>
              <div className="flex flex-wrap items-center gap-8 mt-8">
                {LOGOS.map((logo, i) => (
                  <img key={i} src={logo.src} alt={logo.alt} className="h-6 md:h-8 w-auto object-contain invert grayscale opacity-50 hover:opacity-80 transition-all duration-300 mix-blend-multiply" />
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 7. CZY TO DLA CIEBIE ═══ */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-6 block">SZYBKI SPRAWDZIAN</span>
            <h2 className="mb-6">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">CZY TEN SPRINT</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">jest odpowiedni dla Twojego biznesu?</span>
            </h2>
            <p className="font-body text-base text-[#6B6560] leading-[1.7] max-w-2xl mb-12">
              Ten sprint działa dla każdej osoby prowadzącej biznes, która ma obecność w sieci i płacących klientów. Strona, aktywne social media albo jedno i drugie. Nie potrzebujesz dużego zespołu ani dopracowanej marki, żeby się zakwalifikować.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-[#f5f5f7] rounded-xl p-8 md:p-10">
                <div className="flex items-center gap-2 mb-6">
                  <span className="w-6 h-6 rounded-full bg-[#16a34a] flex items-center justify-center"><Check size={14} className="text-white" /></span>
                  <span className="font-eyebrow text-[#16a34a]">TO JEST DLA CIEBIE, JEŚLI</span>
                </div>
                <div className="space-y-3">
                  {[
                    "Prowadzisz biznes z płacącymi klientami i jesteś gotowa przenieść swoją markę na wyższy poziom",
                    "Chcesz szczerego spojrzenia z zewnątrz na to, jak Twoja marka jest odbierana",
                    "Chcesz jasny punkt startowy zanim zainwestujesz w pełny rebranding albo nową stronę",
                    "Możesz poświęcić dwie godziny w tym miesiącu, żeby uzyskać pełną jasność co do tego, gdzie stoisz",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <Check size={14} className="text-[#16a34a] mt-1 flex-shrink-0" />
                      <span className="font-body text-sm text-[#4A4540]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#f5f5f7] rounded-xl p-8 md:p-10">
                <div className="flex items-center gap-2 mb-6">
                  <span className="w-6 h-6 rounded-full bg-[#BE123C]/15 flex items-center justify-center"><X size={14} className="text-[#BE123C]" /></span>
                  <span className="font-eyebrow text-[#1C1210]">TO NIE JEST DLA CIEBIE, JEŚLI</span>
                </div>
                <div className="space-y-3">
                  {[
                    "Szukasz kogoś, kto powie Ci że wszystko jest w porządku i Twoja marka potrzebuje tylko drobnej korekty",
                    "Chcesz redesign logo albo szybki wizualny fix bez zrozumienia strategii, która za tym stoi",
                    "Nie jesteś gotowa na szczery feedback o tym, jak Twoja marka jest odbierana przez obcych ludzi w tej chwili",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <X size={14} className="text-[#BE123C]/60 mt-1 flex-shrink-0" />
                      <span className="font-body text-sm text-[#6B6560]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ 8. INWESTYCJA ═══ */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-white mb-6 block">BRAND REALITY CHECK ZACZYNA SIĘ TUTAJ</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-white block leading-[0.95]">TWOJA INWESTYCJA</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">wszystko w cenie</span>
              </h2>
              <p className="font-body text-base text-white/60 leading-[1.7] max-w-md mb-4">
                Większość właścicieli biznesu spędza miesiące zastanawiając się, co jest nie tak z ich marką. Ten sprint daje Ci pełny obraz w 7 dni. Jasne odkrycia powiązane z realnymi konsekwencjami biznesowymi i plan, na który możesz działać od razu.
              </p>
              <p className="font-body text-sm text-white/40 mb-10">
                Na bazie 15+ lat strategii marki na 100+ biznesach.
              </p>
              <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-bold text-xs tracking-[2px] uppercase px-10 py-5 hover:bg-[#BE123C] transition-colors duration-300">
                ZAMÓW SPRINT <ArrowRight size={16} />
              </a>
              <p className="font-body text-xs text-white/30 mt-4">Zapłać z góry albo w trzech ratach. Wybierasz na stronie płatności.</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="border border-white/10 rounded-xl p-8 md:p-10">
                <span className="font-eyebrow text-white/50 mb-6 block">CO JEST W CENIE</span>
                <div className="space-y-4">
                  {[
                    "Kwestionariusz wstępny i rozmowa strategiczna (45 min)",
                    "Diagnostyka marki w 6 wymiarach",
                    "Analiza konkurencji (3-5 konkurentów)",
                    "\"Co naprawdę sprzedajesz\" — reframe",
                    "Analiza luk komunikacyjnych",
                    "Analiza uwagi wizualnej (test 5 sekund)",
                    "Mapa napięć strategicznych",
                    "Szczery briefing od mnie (moja osobista diagnoza)",
                    "30-dniowy plan działania",
                    "Rozmowa podsumowująca (45 min)",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check size={16} className="text-[#BE123C] flex-shrink-0 mt-0.5" />
                      <span className="font-body text-sm text-white/80">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ CO DALEJ ═══ */}
      <section className="py-20 md:py-28 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-6 block">CO DALEJ</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">OD DIAGNOZY</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">do pełnego systemu marki</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7]">
                <p>Część osób bierze raport i zaczyna wdrażać tego samego wieczoru.</p>
                <p>Inne odkrywają, że chcą zamienić diagnozę w pełny system marki. Jeśli to Ty, Brand Foundation jest naturalnym następnym krokiem. Wiele osób łączy oba, żeby pracować z jedną osobą od diagnozy do gotowej marki.</p>
              </div>
              <div className="mt-8">
                <Link to="/pl/brand-foundation" className="inline-flex items-center justify-center gap-2 border border-[#1C1210]/20 text-[#1C1210] font-body font-bold text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#1C1210]/5 transition-colors duration-300">
                  POZNAJ BRAND FOUNDATION <ArrowRight size={16} />
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="space-y-4">
                <span className="font-eyebrow text-[#1C1210] mb-4 block">BRAND FOUNDATION OBEJMUJE</span>
                {[
                  "Sześć autorskich ćwiczeń strategicznych w ciągu miesiąca",
                  "Pogłębione wywiady strategiczne z Twoimi klientami",
                  "Zdefiniowane wartości marki, charakter i pozycjonowanie",
                  "Profil idealnego klienta zbudowany na realnych danych",
                  "Ekstrakcja głosu marki i języka komunikacji",
                  "Pełny Brand Foundation Report (Twój brand book)",
                  "Osobisty agent AI wytrenowany na Twoim głosie",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={16} className="text-[#BE123C] flex-shrink-0 mt-0.5" />
                    <span className="font-body text-sm text-[#4A4540]">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 9. FAQ ═══ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-6 block">ZASTANAWIASZ SIĘ?</span>
            <h2 className="mb-10">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">PYTANIA</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">które padają przed zakupem</span>
            </h2>
            <div>
              <FaqItem q="Mój biznes jest mały. Czy ten sprint jest dla mnie?" a="Wtedy jest nawet bardziej istotny. Każda godzina, którą spędzasz zgadując co jest nie tak z Twoją marką, to godzina którą mogłabyś poświęcić klientom. Pełen obraz w 7 dni bez konieczności zostania ekspertką od brandingu." />
              <FaqItem q="Wydaje mi się, że już wiem co jest nie tak z moją marką. Czy to mi się przyda?" a="Większość właścicieli biznesu ma przeczucie, no ale nie potrafi go precyzyjnie nazwać. Jedna z moich klientek była przekonana, że jej problem to logo. Sprint pokazał, że prawdziwy problem polegał na tym, że jej strona cenowa przeczyła stronie głównej. Czuć, że coś jest nie tak i zobaczyć konkretne zdanie, które niszczy zaufanie to dwie różne rzeczy." />
              <FaqItem q="Już próbowałam sama analizować swoją markę. Czym to się różni?" a="Jeśli już patrzyłaś na swoją markę i wciąż czujesz się niepewna, ten sprint został zaprojektowany właśnie na taką sytuację. Wnoszę 15 lat rozpoznawania wzorców na 100+ biznesach i perspektywę z zewnątrz, której sama sobie nie dasz." />
              <FaqItem q="Czy to jest kolejny generyczny audyt marki?" a="Nie. Każdy sprint jest budowany wokół Twojego biznesu, Twojej konkurencji i Twoich odbiorców. Odkrycia są powiązane z tym, ile każdy problem kosztuje Cię w przychodach, nie z rekomendacjami które mogłabyś znaleźć na blogu." />
              <FaqItem q="Co jeśli potrzebuję pomocy we wdrożeniu rekomendacji?" a="Sprint jest zaprojektowany tak, żeby stał samodzielnie. 30-dniowy plan daje Ci pięć kroków uszeregowanych według wpływu, które możesz zacząć w tym tygodniu. No ale jeśli chcesz pójść głębiej i zbudować pełny system marki, Brand Foundation jest naturalnym następnym krokiem." />
              <FaqItem q="Co się dzieje po kliknięciu przycisku?" a="Przechodzisz na stronę płatności, gdzie możesz wybrać płatność z góry albo w trzech ratach. Po potwierdzeniu płatności wysyłam Ci kwestionariusz wstępny w ciągu 24 godzin i umawiamy pierwszą rozmowę." />
              <FaqItem q="Jakie są opcje płatności?" a="Możesz zapłacić pełną kwotę z góry albo rozłożyć ją na trzy miesięczne raty. Obie opcje są dostępne na stronie płatności." />
              <FaqItem q="Mam jeszcze pytania zanim się zdecyduję. Możemy porozmawiać?" a="Jasne. Możesz umówić bezpłatną 15-minutową rozmowę, na której odpowiem na Twoje pytania i pomogę Ci zdecydować, czy ten sprint to odpowiedni następny krok." />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ 10. FINAL CTA ═══ */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/sand-drawing.jpg" alt="" className="w-full h-full object-cover object-right" />
          <div className="absolute inset-0 bg-[#1C1210]/60 lg:bg-transparent" />
          <div className="hidden lg:block absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(28,18,16,0.92) 30%, rgba(28,18,16,0.5) 50%, transparent 70%)' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-md">
            <h2 className="mb-6">
              <span className="font-display text-3xl md:text-4xl text-white block leading-[0.95]">GOTOWA ZOBACZYĆ</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-2">co naprawdę mówi Twoja marka?</span>
            </h2>
            <p className="font-body text-base text-white/70 leading-relaxed mb-10">
              Przeczytałaś wystarczająco, żeby wiedzieć czy to pasuje. Jeśli intuicja mówi Ci, że to ten moment, zaufaj jej. Za siedem dni będziesz dokładnie wiedzieć, gdzie stoisz i co z tym zrobić.
            </p>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-bold text-xs tracking-[2px] uppercase px-10 py-5 hover:bg-[#BE123C] transition-colors duration-300">
              ZAMÓW SPRINT <ArrowUpRight size={16} />
            </a>
            <p className="font-body text-xs text-white/30 mt-4">Zarezerwuj swoje miejsce. Ograniczona liczba sprintów miesięcznie.</p>
          </div>
        </div>
      </section>
    </main>
  );
}
